<template>
  <div class="pagination">
    <ul class="pagination__list">
      <li class="pagination__list__item" :class="{ disabled: disablePrev }">
        <a class="pagination__list__item__link previous__button" @click="handlePreviousPage()">
          <icon class-name="pagination__list__item previous__button__icon" name="chevron-left" />
          Previous
        </a>
      </li>
      <li
        v-for="page in paginationConfiguration.left"
        :key="page"
        class="pagination__list__item"
        :class="{ active: page + 1 == currentPage }"
      >
        <a
          class="pagination__list__item__link button__left"
          @click="handlePageSelected(page)"
        >{{ page + 1 }}</a>
      </li>
      <li v-if="paginationConfiguration.breakView">
        <a
          class="pagination__list__item__link button"
          @click="handleBreakClick(paginationConfiguration.breakViewIndex)"
        >...</a>
      </li>
      <li
        v-for="page in paginationConfiguration.right"
        :key="page"
        class="pagination__list__item"
        :class="{ active: page + 1 == currentPage }"
      >
        <a
          class="pagination__list__item__link button__right"
          @click="handlePageSelected(page)"
        >{{ page + 1 }}</a>
      </li>
      <li class="pagination__list__item" :class="{ disabled: disableNext }">
        <a class="pagination__list__item__link next__button" @click="handleNextPage()">
          Next
          <icon class-name="pagination__list__item next__button__icon" name="chevron-right" />
        </a>
      </li>
      <li v-if="pageSizeList.length" class="page-size-select">
        <form-select
          :options="getPageSizeDropdownList"
          :value="{value: pageSize}"
          @input="onPageSizeChange"
        />
      </li>
    </ul>
  </div>
</template>

<script>
// Components
import Icon from "../Icon/Icon.vue";
import FormSelect from "../mds/FormSelect.vue";

// Helpers
import { generatePaginationConfiguration } from "../../utils/helpers";
export default {
  name: "Pagination",
  components: {
    Icon,
    FormSelect
  },
  props: {
    /**
     * @description
     * Total Items for Pagination, Change updates the Pagination Config.
     */
    totalItems: {
      type: Number,
      required: true,
    },
    /**
     * @description
     * Current Page, Used to Hightlight the Correct Page and generate the Pagination Config.
     */
    currentPage: {
      type: Number,
      required: true,
    },
    /**
     * @description
     * Total number of items in a Page. Useful in identifying the start count
     */
    pageSize: {
      type: Number,
      required: true,
    },
    /**
     * @description
     * Total Number of Pages, It is used to generate the Pagination Config.
     */
    pageCount: {
      type: Number,
      required: true,
    },
    pageSizeList: {
      type: Array,
      default: () => [],
    }
  },
  data() {
    return {
      /**
       * @description
       * State for Pagination Config from the Helper Method, Prop Changes trigger Config Update using the watcher.
       */
      paginationConfiguration: generatePaginationConfiguration(
        this.currentPage,
        this.pageCount
      ),
    };
  },
  computed: {
    disablePrev() {
      return this.currentPage - 1 === 0;
    },
    disableNext() {
      return this.currentPage - 1 === this.pageCount - 1;
    },
    combinedResult() {
      return [this.totalItems + this.currentPage];
    },
    getPageSizeDropdownList() {
      return this.pageSizeList.map((item) => ({
        label: `${item}/per page`,
        value: item
      }))
    },
  },
  watch: {
    combinedResult() {
      this.paginationConfiguration = generatePaginationConfiguration(
        this.currentPage,
        this.pageCount
      );
    },
    pageSize() {
        this.paginationConfiguration = generatePaginationConfiguration(
        this.currentPage,
        this.pageCount
      );
    }
  },
  methods: {
    /**
     * @description
     * Emits the Previous Page Number
     */
    handlePreviousPage() {
      if (this.currentPage > 1) {
        this.$emit("onPageChange", this.currentPage - 1);
      }
    },
    /**
     * @description
     * Emits the Next Page Number
     */
    handleNextPage() {
      if (this.currentPage < this.pageCount) {
        this.$emit("onPageChange", this.currentPage + 1);
      }
    },
    /**
     * @description
     * Emits the Current Page Number
     */
    handlePageSelected(index) {
      let current = index + 1;
      if (this.currentPage === current) return;
      this.$emit("onPageChange", current);
    },
    /**
     * @description
     * Emits the 2 after the current page Number
     */
    getForwardJump() {
      const forwardJump = this.currentPage + 2;
      const forwardJumpPageNumber =
        forwardJump >= this.pageCount ? this.pageCount - 1 : forwardJump;
      return forwardJumpPageNumber;
    },
    /**
     * @description
     * Emits the 2 before the current page Number
     */
    getBackwardJump() {
      const backwardJump = this.currentPage - 2;
      const backwardJumpPageNumber = backwardJump < 0 ? 0 : backwardJump;
      return backwardJumpPageNumber;
    },
    /**
     * @description
     * Emits the 2 before the current page Number
     */
    handleBreakClick(index) {
      this.handlePageSelected(
        this.currentPage < index
          ? this.getForwardJump()
          : this.getBackwardJump()
      );
    },
    onPageSizeChange(e) {
      this.$emit('onChangeItemsPerPage', e);
    }
  },
};
</script>

<style lang="scss" scoped>
.pagination {
  &__list {
    display: flex;

    li {
      list-style: none;

      a {
        color: var(--Gray-900);
        font-family: "Maersk Text";
        font-size: 1rem;
        background-color: var(--Gray-200);
        padding: 1rem;
        height: 2rem;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        line-height: 0;
        border-radius: 0.25rem;
        margin-right: 0.5rem;

        @media (max-width: 800px) {
          margin-right: 0.5rem;
          font-size: 0.875rem;
        }

        @media (max-width: 800px) {
          padding: 2px 6px;
          margin-right: 0.25rem;
        }

        &:last {
          margin-right: 0;
        }

        &:hover {
          background-color: var(--Blue-100);
          color: var(--Gray-50);
        }
      }

      &.disabled {
        a {
          cursor: not-allowed;
          background-color: var(--Gray-200);
          color: var(--Gray-900);
        }
      }

      &.active {
        a {
          position: relative;
          background-color: var(--Blue-300-Maersk);
          color: var(--Gray-50);
          font-family: "Maersk Text Bold";

          &:after {
            background-color: var(--Blue-600-Dark);
            content: "";
            height: 0.25rem;
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            border-bottom-left-radius: 0.25rem;
            border-bottom-right-radius: 0.25rem;
          }
        }
      }
    }

    & .previous__button,
    & .next__button {
      background-color: var(--Gray-100);
      color: var(--Gray-900);
    }
  }
}
</style>

<style lang="scss">
.page-size-select .form-input {
  select{
    height: 32px!important;
    background-color: var(--Gray-100);
    color: var(--Gray-900);
    padding-top: 0px;
    padding-bottom: 5px;
  }
  i {
    color: #333;
    z-index: 2;
  }
}
</style>
